import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { PageHeader, SectionCard, EmptyState } from "@/components/common/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Tags } from "lucide-react";

export default function WawiCategories() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const admin = can(user?.role, "wawi:admin");
  const [edit, setEdit] = useState(null);
  const { data } = useQuery({ queryKey: ["wawi-categories"], queryFn: async () => (await api.get("/wawi/categories")).data });
  const cats = data?.items || [];
  const nameById = Object.fromEntries(cats.map((c) => [c.id, c.name]));

  const save = async () => {
    if (!edit.name.trim()) return toast.error("Bitte Name eingeben");
    try {
      if (edit.id) await api.put(`/wawi/categories/${edit.id}`, { name: edit.name, parent: edit.parent || "" });
      else await api.post("/wawi/categories", { name: edit.name, parent: edit.parent || "" });
      toast.success("Kategorie gespeichert"); setEdit(null); qc.invalidateQueries({ queryKey: ["wawi-categories"] });
    } catch (err) { toast.error(apiError(err)); }
  };
  const del = async (id) => { try { await api.delete(`/wawi/categories/${id}`); qc.invalidateQueries({ queryKey: ["wawi-categories"] }); } catch (err) { toast.error(apiError(err)); } };

  return (
    <div>
      <PageHeader title="Kategorien" description="Artikelkategorien für Geräte und Zubehör"
        actions={admin && <Button className="gap-1.5 bg-accent text-accent-foreground hover:bg-accent/90" onClick={() => setEdit({ name: "", parent: "" })} data-testid="new-category-button"><Plus className="h-4 w-4" />Neue Kategorie</Button>} />
      <SectionCard title={`${cats.length} Kategorien`}>
        {cats.length === 0 ? <EmptyState icon={Tags} title="Keine Kategorien" description="Legen Sie Kategorien wie Smartphones, Zubehör, Kabel an." /> : (
          <div className="space-y-2">
            {cats.map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-md border p-3" data-testid={`category-row-${c.id}`}>
                <div className="flex items-center gap-2">
                  <Tags className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">{c.name}</span>
                  {c.parent && <Badge variant="outline" className="text-[10px]">↳ {nameById[c.parent] || "—"}</Badge>}
                </div>
                {admin && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEdit({ ...c })}>Bearbeiten</Button>
                    <Button size="sm" variant="outline" className="text-destructive" onClick={() => del(c.id)}>Löschen</Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>{edit?.id ? "Kategorie bearbeiten" : "Neue Kategorie"}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Name</Label><Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="category-name-input" /></div>
              <div className="space-y-1.5"><Label>Übergeordnete Kategorie (optional)</Label>
                <Select value={edit.parent || "none"} onValueChange={(v) => setEdit({ ...edit, parent: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="category-parent"><SelectValue placeholder="Keine" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Keine</SelectItem>
                    {cats.filter((c) => c.id !== edit.id).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>Abbrechen</Button>
            <Button onClick={save} className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="category-save-button">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
